// IMPORT React
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../../components/CustomFields/TextField";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddIcon from "@mui/icons-material/Add";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import { PreferencesContext } from "../../../PreferenceContext";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import DraggableList from "../../../components/Draggable/DraggableList";
import VaccineTemplateDoseSave from "./VaccineTemplateDose";
import { format } from "date-fns";
import {
  CreateVaccineTemplate,
  GetVaccineTemplateDetail,
  UpdateVaccineTemplate,
} from "../../../services/VaccineTemplateService";

// SCHEMA FOR DETAIL COLL OF FORM
interface IFormDetail {
  vaccineTemplateDoseId: number;
  vaccineId: number;
  displayOrder: number;
  isFirstDose: boolean;
  doseOder: number;
  doseName: string;
  firstDoseRefCode: string;
  firstDoseRefName: string;
  firstDoseRefDate: Date | null;
  firstDoseAgeValue: number;
  firstDoseAgeMode: string | null;
  firstDoseRefVaccineDoseId: number;
  durationBetweenPreviousCurrentDoseValue: number;
  durationBetweenPreviousCurrentDoseMode: string | null;
  standardDurationBetweenPreviousCurrentDoseValue: number;
  standardDurationBetweenPreviousCurrentDoseMode: string | null;
  maxCatchUpAgeValue: number;
  maxCatchUpAgeMode: string | null;
  isDeleted: boolean;
  mode: string;
  vaccineGroupId: number;
  vaccineGroupName: string;
}

// SCHEMA FOR FORM
interface formInitialValueTypes {
  vaccineTemplateId: number;
  consumerId: number;
  orgId: number;
  branchId: number;
  templateName: string;
  vaccineDoseColl: Array<IFormDetail>;
  isDeleted: boolean;
}

type Order = "asc" | "desc" | "";
interface EnhancedTableProps {
  numSelected: number;
  //onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: String;
  rowCount: number;
}

const skeletonRowCount = 5;

export default function VaccineTemplateSave(Props: any) {
  const {
    isForPopUp = false,
    handleClosePopUp,
    name,
    opd,
    popUpReason,
    code,
  } = Props;
  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const theme = useTheme();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }
  const mdd = useMediaQuery(theme.breakpoints.down("md"));
  // DEFAULT SCHEMA VALUE FOR FORM
  const initVaccineData: formInitialValueTypes = {
    vaccineTemplateId: 0,
    consumerId: 0,
    branchId: 0,
    orgId: 0,
    templateName: isForPopUp ? name?.toUpperCase() : "",
    vaccineDoseColl: [],
    isDeleted: false,
  };

  // GETTING PARAMS
  let urlParams = useQuery();
  let curRawVaccineId = urlParams.get("q");
  let vaccineTemplateId = 0;
  if (curRawVaccineId !== null && curRawVaccineId !== undefined) {
    let decId = edService.decrypt(curRawVaccineId);
    if (decId !== "" && decId !== null && decId !== undefined && !isForPopUp) {
      vaccineTemplateId = +decId;
    }
  } else {
    // if (urlParams.size > 0) {
    //     navigate("/not-found");
    // }
  }

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
    code: "",
    dose: null,
    option: null,
  });

  const [vaccineData, setVaccineData] = React.useState<any>(null);

  // TABLE HEADER
  function EnhancedTableHead(props: EnhancedTableProps) {
    return (
      <TableHead
        className="table-header-color"
        sx={{ backgroundColor: "#e1eaf1" }}
      >
        <TableRow>
          <TableCell
            sx={{
              fontWeight: "bold",
            }}
            align="left"
          >
            Dose Name
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
            }}
            align="left"
          >
            Vaccine age Group
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
            }}
            align="left"
          >
            First Dose Ref
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
            }}
            align="left"
          >
            First Dose Ref Date
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
            }}
            align="left"
          >
            First Dose Ref Age
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
            }}
            align="left"
          >
            Duration Between Dose
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
            }}
            align="left"
          >
            Max Catch Up Age
          </TableCell>
        </TableRow>
      </TableHead>
    );
  }

  const VaccineDoseCard = ({ item }: { item: any }) => {
    const [anchorEl3, setAnchorEl3] = React.useState<HTMLButtonElement | null>(
      null
    );
    const [openDeleteDilog, setOpenDeleteDilog] = React.useState(false);
    const [vaccineId, setVaccineId] = useState(0);
    const [doseItem, setDoseItem] = useState(0);

    const vaccineDoseColl = methods.getValues("vaccineDoseColl") || [];

    const dependentOption = vaccineDoseColl.map((dose: any) => ({
      value: dose.vaccineId,
      label: `${dose.vaccineGenericName} - ${dose.doseName}`,
      vaccineDoseName: dose.doseName,
    }));

    // HANDLE POPUP FOR EDIT AND DELETE
    const handleOpenMore = (
      event: React.MouseEvent<HTMLButtonElement>,
      vaccineId: any,
      item: any
    ) => {
      event.stopPropagation();
      setAnchorEl3(event.currentTarget);
      setVaccineId(vaccineId);
      setDoseItem(item);
    };

    const handleCloseMore = () => {
      setAnchorEl3(null);
    };

    const handleDeleteDoseGroup = (vaccineId: number) => {
      setAnchorEl3(null);
      handleDeleteDilog(false);
      const currentDoseColl = methods.getValues("vaccineDoseColl") || [];
      const remainingDoses = currentDoseColl.map((dose: any) => {
        if (dose.vaccineId === vaccineId) {
          return {
            ...dose,
            isDeleted: true,
            mode: dose.vaccineTemplateDoseId > 0 ? "D" : "I",
          };
        }
        return dose;
      });

      methods.setValue("vaccineDoseColl", remainingDoses, {
        shouldValidate: true,
      });
      methods.trigger("vaccineDoseColl");
    };

    const handleDeleteDilog = (value: any) => {
      setOpenDeleteDilog(value);
    };

    if (!item || !item.doses?.length) return null;

    return (
      <Box
        sx={{
          border: "1px solid #ddd",
          borderRadius: "8px",
          padding: "12px 16px",
          backgroundColor: "#fafafa",
          margin: "5px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            borderBottom: "1px solid #eee",
          }}
        >
          <Typography
            className="text-link"
            variant="h6"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Box
              component="span"
              sx={{ fontWeight: 600 }}
              onClick={() =>
                handleClickOpenPopUp(
                  "Edit Vaccine Dose",
                  "DOSE",
                  item,
                  dependentOption
                )
              }
            >
              {item.vaccineGenericName}
            </Box>
          </Typography>
          <Box>
            <Tooltip title="More" placement="top">
              <IconButton
                onClick={(e: any) => {
                  e.stopPropagation();
                  handleOpenMore(e, item?.vaccineId || 0, item);
                }}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {!mdd ? (
          <Box>
            <TableContainer>
              <Table
                size="small"
                sx={{
                  width: "100%",
                  tableLayout: "fixed",
                }}
              >
                <EnhancedTableHead />
                <TableBody>
                  {item.doses.map((dose: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>{dose.doseName}</TableCell>
                      <TableCell>{dose.vaccineGroupName || "-"}</TableCell>
                      <TableCell>{dose.firstDoseRefName || "-"}</TableCell>
                      <TableCell>
                        {dose.firstDoseRefDate
                          ? (() => {
                              try {
                                const date = new Date(dose.firstDoseRefDate);
                                return isNaN(date.getTime()) ? "-" : format(date, "dd/MM/yyyy");
                              } catch {
                                return "-";
                              }
                            })()
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {dose.firstDoseAgeValue || "-"}{" "}
                        {dose.firstDoseAgeMode || ""}
                      </TableCell>
                      <TableCell>
                        {dose.durationBetweenPreviousCurrentDoseValue || "-"}{" "}
                        {dose.durationBetweenPreviousCurrentDoseMode || ""}
                      </TableCell>
                      <TableCell>
                        {dose.maxCatchUpAgeValue || "-"}{" "}
                        {dose.maxCatchUpAgeMode || ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Box>
            {item.doses.map((dose: any, index: number) => (
              <Box
                key={index}
                sx={{
                  backgroundColor: "#f9f9f9",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  padding: "12px",
                  marginBottom: "10px",
                }}
              >
                <Box
                  sx={{
                    fontWeight: "bold",
                    fontSize: "15px",
                    marginBottom: "8px",
                    color: "#333",
                  }}
                >
                  {dose.doseName}
                </Box>
                <Box
                  sx={{ fontSize: "13px", color: "#666", marginBottom: "6px" }}
                >
                  <strong>Vaccine Group:</strong> {dose.vaccineGroupName || "-"}
                </Box>
                <Box
                  sx={{ fontSize: "13px", color: "#666", marginBottom: "6px" }}
                >
                  <strong>First Dose Ref:</strong>{" "}
                  {dose.firstDoseRefName || "-"}
                </Box>
                <Box
                  sx={{ fontSize: "13px", color: "#666", marginBottom: "6px" }}
                >
                  <strong>Ref Date:</strong>{" "}
                  {dose.firstDoseRefDate
                    ? (() => {
                        try {
                          const date = new Date(dose.firstDoseRefDate);
                          return isNaN(date.getTime()) ? "-" : format(date, "dd/MM/yyyy");
                        } catch {
                          return "-";
                        }
                      })()
                    : "-"}
                </Box>
                <Box
                  sx={{ fontSize: "13px", color: "#666", marginBottom: "6px" }}
                >
                  <strong>Age:</strong> {dose.firstDoseAgeValue || "-"}{" "}
                  {dose.firstDoseAgeMode || ""}
                </Box>
                <Box
                  sx={{ fontSize: "13px", color: "#666", marginBottom: "6px" }}
                >
                  <strong>Duration:</strong>{" "}
                  {dose.durationBetweenPreviousCurrentDoseValue || "-"}{" "}
                  {dose.durationBetweenPreviousCurrentDoseMode || ""}
                </Box>
                <Box sx={{ fontSize: "13px", color: "#666" }}>
                  <strong>Max Catch Up:</strong>{" "}
                  {dose.maxCatchUpAgeValue || "-"}{" "}
                  {dose.maxCatchUpAgeMode || ""}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Popover
          elevation={1}
          open={Boolean(anchorEl3)}
          anchorEl={anchorEl3}
          onClose={handleCloseMore}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          sx={{
            "& .MuiPopover-paper": {
              border: "1px solid #ced4da",
              borderRadius: "5px",
            },
          }}
        >
          <List>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() =>
                  handleClickOpenPopUp(
                    "Edit Vaccine Dose",
                    "DOSE",
                    doseItem,
                    dependentOption
                  )
                }
              >
                <ListItemText primary="Edit" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                className="delete-container"
                onClick={() => {
                  handleDeleteDilog(true);
                  handleCloseMore();
                }}
              >
                <ListItemText primary="Delete" />
              </ListItemButton>
            </ListItem>
          </List>
        </Popover>
        <Dialog
          open={openDeleteDilog}
          onClose={() => {
            handleDeleteDilog(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this vaccine doses?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => handleDeleteDoseGroup(vaccineId)}
              className="button-delete"
            >
              Yes
            </Button>
            <Button
              onClick={(e) => {
                handleDeleteDilog(false);
              }}
              autoFocus
            >
              No
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  const handleManagePopup = async (code: string, data?: any) => {
    setCustomPopUp({ ...customPopUp, open: false });
    setIsLoading(true);

    if (code === "CREATE_DOSE" && data && data.vaccineId) {
      const currentDoseColl = methods.getValues("vaccineDoseColl") || [];
      const newDoseColl = data.vaccineDoseColl;

      const remainingDoses = currentDoseColl.filter(
        (dose: any) => dose.vaccineId !== data.vaccineId.value
      );

      const updatedGroupDoses = newDoseColl.map((dose: any, index: number) => ({
        ...dose,
        vaccineId: data.vaccineId.value,
        vaccineGenericName: data.vaccineGenericName,
        standardDurationBetweenPreviousCurrentDoseValue:
          data.standardDurationBetweenPreviousCurrentDoseValue,
        standardDurationBetweenPreviousCurrentDoseMode:
          data.standardDurationBetweenPreviousCurrentDoseMode,
        displayOrder: index,
        mode: dose.vaccineTemplateDoseId > 0 ? "U" : "I",
      }));

      const finalDoseCollection = [...remainingDoses, ...updatedGroupDoses];

      methods.setValue("vaccineDoseColl", finalDoseCollection, {
        shouldValidate: true,
      });

      const updatedFormData = {
        ...methods.getValues(),
        vaccineDoseColl: finalDoseCollection,
      };

      setVaccineData((prev: any) => ({
        ...prev,
        vaccineDoseColl: finalDoseCollection,
      }));
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: "Vaccine doses updated successfully.",
        alertType: AlertType.Success,
      });
    } else if (code === "CLOSE") {
    }

    setIsLoading(false);
  };

  const handleClickOpenPopUp = (
    label: string,
    code: string,
    dose: any,
    option: any
  ) => {
    setCustomPopUp({
      ...customPopUp,
      open: true,
      name: label,
      code: code,
      dose: dose,
      option: option,
    });
  };

  const handleClosePopup = (data?: any) => {
    setCustomPopUp({ ...customPopUp, open: false });
  };

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    vaccineTemplateId: Yup.number().nullable(),
    consumerId: Yup.number().required(),
    branchId: Yup.number().required(),
    orgId: Yup.number().required(),
    templateName: Yup.string().required("Vaccine Template is required"),
    vaccineDoseColl: Yup.array().of(Yup.object().shape({})),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initVaccineData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  const WatchFields = useWatch({
    control: methods.control,
  });

  const vaccineDoseColl = WatchFields.vaccineDoseColl;

  const dependentOption = (vaccineDoseColl || []).map((dose: any) => ({
    value: dose.vaccineId,
    label: `${dose.vaccineGenericName} - ${dose.doseName}`,
    vaccineDoseName: dose.doseName,
  }));

  const filteredVaccineBrandDetailColl = (vaccineDoseColl || [])
    .filter((x: any) => x?.isDeleted === false && x.mode !== "D")
    .reduce((acc: any[], curr: any) => {
      const existing = acc.find((v) => v.vaccineId === curr.vaccineId);
      if (existing) {
        existing.doses.push(curr);
      } else {
        acc.push({
          vaccineId: curr.vaccineId,
          vaccineGenericName: curr.vaccineGenericName,
          standardDurationBetweenPreviousCurrentDoseValue:
            curr.standardDurationBetweenPreviousCurrentDoseValue,
          standardDurationBetweenPreviousCurrentDoseMode:
            curr.standardDurationBetweenPreviousCurrentDoseMode,
          doses: [curr],
        });
      }
      return acc;
    }, []);

  const handleVaccineBrandDetailChange = (value: any) => {
    const { dragIndex, hoverIndex } = value;
    const vaccineDoseColl = methods.getValues().vaccineDoseColl || [];

    // Clone the array to avoid direct mutation
    const updatedItems = [...vaccineDoseColl];

    // Get the item at dragIndex
    const draggedItem = updatedItems[dragIndex];

    // Remove the item from its original position (dragIndex)
    updatedItems.splice(dragIndex, 1);

    // Insert the item at hoverIndex
    updatedItems.splice(hoverIndex, 0, draggedItem);

    // Update the displayOrder of each item in the updated array
    const updatedItemsWithDisplayOrder = updatedItems.map((item, index) => {
      return {
        ...item,
        displayOrder: index, // Adjust this if needed to fit your use case
      };
    });

    methods.setValue("vaccineDoseColl", updatedItemsWithDisplayOrder, {
      shouldValidate: true,
    });
  };

  // GET VACCINE
  const getVaccineDetail = async () => {
    let item: any;
    let params = new URLSearchParams();
    params.append("vaccineTemplateId", vaccineTemplateId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());

    try {
      const res: any = await GetVaccineTemplateDetail(params);
      item = res.data.data;
      const mappedDoses = (item?.vaccineDossColl || []).map((dose: any) => {
        const doseOrderValue = dose.doseOrder || dose.doseOder || 0;

        return {
          ...dose,
          displayOrder: doseOrderValue,
          firstDoseAgeValue: Number(dose.firstDoseAgeValue) || 0,
          durationBetweenPreviousCurrentDoseValue:
            Number(dose.durationBetweenPreviousCurrentDoseValue) || 0,
          standardDurationBetweenPreviousCurrentDoseValue:
            Number(dose.standardDurationBetweenPreviousCurrentDoseValue) || 0,
          maxCatchUpAgeValue: Number(dose.maxCatchUpAgeValue) || 0,
          mode: dose.vaccineTemplateDoseId > 0 ? "U" : "I",
          isFirstDose: dose.isFirstDose === true,
          vaccineGroupId: dose.vaccineGroupId,
          vaccineGroupName: dose.vaccineGroupName,
        };
      });

      methods.reset({
        ...initVaccineData,
        vaccineTemplateId: item?.vaccineTemplateId || 0,
        templateName: item?.templateName,
        vaccineDoseColl: mappedDoses,
      });
      return item;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE VACCINE
  const saveVaccineTemplate = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      const cleanedDoseColl = value.vaccineDoseColl
        .filter((d) => d.mode !== "I" || d.isDeleted === false)
        .map((dose) => ({
          vaccineTemplateDoseId: dose.vaccineTemplateDoseId,
          vaccineId: dose.vaccineId,
          vaccineGroupId: dose.vaccineGroupId,
          vaccineGroupName: dose.vaccineGroupName,
          isFirstDose: dose.isFirstDose,
          doseOrder: dose.displayOrder,
          doseName: dose.doseName,
          firstDoseRefCode: dose.firstDoseRefCode,
          firstDoseRefName: dose.firstDoseRefName,
          firstDoseRefData: dose.firstDoseRefDate,
          firstDoseRefVaccineDoseId: dose.firstDoseRefVaccineDoseId,
          firstDoseAgeValue: dose.firstDoseAgeValue,
          firstDoseAgeMode: dose.firstDoseAgeMode,
          durationBetweenPrevCurrentDoseValue:
            dose.durationBetweenPreviousCurrentDoseValue,
          durationBetweenPrevCurrentDoseMode:
            dose.durationBetweenPreviousCurrentDoseMode,
          standardDurationBetweenPrevCurrentDoseValue:
            dose.standardDurationBetweenPreviousCurrentDoseValue,
          standardDurationBetweenPrevCurrentDoseMode:
            dose.standardDurationBetweenPreviousCurrentDoseMode,
          maxCatchUpAgeValue: dose.maxCatchUpAgeValue,
          maxCatchUpAgeMode: dose.maxCatchUpAgeMode,
          isDeleted: dose.isDeleted,
          mode: dose.mode,
        }));
      let formData = {
        vaccineTemplateId: value.vaccineTemplateId,
        consumerId: userContext?.consumerId,
        orgId: userContext?.organizationId,
        branchId: userContext?.branchId,
        templateName: value.templateName?.trim(),
        templateEnableForCountryId: userContext?.loginDetail?.countryID,
        VaccineDoseDetail: JSON.stringify(cleanedDoseColl),
        isDeleted: value.isDeleted,
        dataManagedBy: userContext.loginDetail.userId,
      };
      if (vaccineTemplateId > 0) {
        result = await UpdateVaccineTemplate(formData);
      } else {
        result = await CreateVaccineTemplate(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result?.data) {
            handleClosePopUp("VACCINE_MODULE_CREATE", result?.data);
          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveVaccineTemplate(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    navigate(-1);
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      if (vaccineTemplateId > 0) {
        const item = await getVaccineDetail();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [vaccineTemplateId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <>
      <Box>
        <Box className={"main-container"}>
          <Grid container className="page-header">
            <Grid item xs={6} sm={6} md={6}>
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  alignItems: "center",
                }}
              >
                <CustomBreadcrumb />
              </Box>
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="outlined"
                  type="button"
                  className="secondary-button"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                >
                  Back
                </Button>
              </Box>
            </Grid>
          </Grid>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} action="">
              <Grid container gap={2}>
                <Grid item xs={12} className="group-container">
                  <Grid item container xs={12} spacing={2}>
                    <Grid item xs={12} sm={6} md={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${
                            methods.formState.errors.templateName
                              ? "validation-message"
                              : ""
                          }`}
                        >
                          Vaccine Template Name *
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder={
                              "Pediatric Dose Template / Adult Dose Template"
                            }
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            id="templateName"
                            size="small"
                            autoFocus={vaccineTemplateId > 0 ? false : true}
                            {...methods.register("templateName", {
                              onChange: (e) => {
                                const upperValue = e.target.value.toUpperCase();
                                e.target.value = upperValue;
                                methods.setValue("templateName", upperValue, {
                                  shouldValidate: true,
                                });
                              },
                            })}
                            error={!!methods.formState.errors.templateName}
                            className={`text-input ${
                              methods.formState.errors.templateName
                                ? "error"
                                : ""
                            }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.templateName?.message}
                      </Typography>
                    </Grid>
                    <Grid container spacing={2} mt={1}>
                      <Grid item xs={12}>
                        <Box
                          className="template-table-container"
                          style={{
                            border: "1px solid #0000001f",
                            borderRadius: "8px",
                            zIndex: 1,
                          }}
                        >
                          <Box className="inner-title-container">
                            {isLoading ? (
                              <>
                                <Grid container spacing={0}>
                                  {Array(skeletonRowCount)
                                    ?.fill(0)
                                    ?.map((skeletonRow, index) => {
                                      return (
                                        <Grid
                                          item
                                          xs={12}
                                          key={
                                            "skeleton-row" + index?.toString()
                                          }
                                          sx={{
                                            borderBottom: "1px solid #ced4da",
                                          }}
                                        >
                                          <SkeletonWrapper
                                            isLoading={isLoading}
                                            variant="rectangular"
                                            className="skeleton-draggable-item"
                                          >
                                            <span></span>
                                          </SkeletonWrapper>
                                        </Grid>
                                      );
                                    })}
                                </Grid>
                              </>
                            ) : (
                              <DraggableList
                                items={filteredVaccineBrandDetailColl || []}
                                setItem={handleVaccineBrandDetailChange}
                                Styledcard={VaccineDoseCard}
                              />
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                padding: "10px",
                                paddingLeft: "20px",
                                flexWrap: "wrap",
                              }}
                            >
                              <Button
                                color="primary"
                                startIcon={<AddIcon />}
                                variant="contained"
                                type="button"
                                className="primary-button"
                                name="add row"
                                onClick={() => {
                                  handleClickOpenPopUp(
                                    "",
                                    "DOSE",
                                    "",
                                    dependentOption
                                  );
                                }}
                              >
                                Add Vaccine Dose
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                    <LoadingButton
                      color="primary"
                      loadingPosition="start"
                      startIcon={<></>}
                      variant="contained"
                      type="submit"
                      className="primary-button"
                      name="save"
                      disabled={showSkeleton || isLoading}
                    >
                      {vaccineTemplateId > 0 ? "Update" : "Save"}
                    </LoadingButton>
                    <LoadingButton
                      color="primary"
                      loadingPosition="start"
                      startIcon={<></>}
                      variant="outlined"
                      type="button"
                      className="secondary-button"
                      name="cancel"
                      onClick={handleBack}
                    >
                      Cancel
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </Box>
      </Box>
      <Dialog
        className="dialogPage"
        open={customPopUp.open && customPopUp.code === "DOSE"}
        onClose={(e, reason) => {
          if (reason !== "backdropClick") {
            handleClosePopup();
          }
        }}
        fullScreen
        // PaperProps={{
        //   style: {
        //     width: "95%",
        //     height: "700px",
        //     maxWidth: "none",
        //   },
        // }}
      >
        <AppBar sx={{ position: "relative", width: "100%" }}>
          <Toolbar>
            <Box
              sx={{
                display: "flex",
                width: "100%",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
                Vaccine
                Template
                Dose
              >
                Vaccine Template Dose
              </Typography>
              <IconButton
                onClick={(e) => {
                  handleClosePopup();
                }}
              >
                <CloseRoundedIcon />
              </IconButton>
            </Box>
          </Toolbar>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </AppBar>
        <DialogContent sx={{ padding: "12px 24px" }}>
          <VaccineTemplateDoseSave
            handleClosePopUp={(code: any, company: any) => {
              handleManagePopup(code, company);
            }}
            dose={customPopUp.dose}
            isForPopUp={customPopUp.open}
            dependentOption={customPopUp.option}
          />
        </DialogContent>
        <Divider />
      </Dialog>
    </>
  );
}
